import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PauseCircle, PlayCircle } from "lucide-react";

const REASONS = [["holiday", "Holiday / travel"], ["illness", "Illness"], ["life", "Life is full right now"], ["other", "Other"]];

export function HabitPause({ habit, onChange }) {
  const [open, setOpen] = useState(false);
  const [until, setUntil] = useState(() => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
  const [reason, setReason] = useState("life");

  const pause = async () => {
    try {
      await api.post(`/habits/${habit.id}/pause`, { until, reason });
      toast.success(`Paused until ${until}. No weeks count as missed while you're away.`);
      setOpen(false);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const resume = async () => {
    await api.post(`/habits/${habit.id}/resume`);
    toast.success("Welcome back. Picking up right where you left off.");
    onChange();
  };

  if (habit.paused_until) {
    return (
      <div className="mt-3 flex items-center justify-between gap-2 rounded-2xl bg-sky-50 border border-sky-200 px-3 py-2" data-testid={`paused-banner-${habit.id}`}>
        <span className="text-xs text-sky-900"><strong>Paused</strong> until {habit.paused_until} ({habit.pause_reason}). Goals won't drop while you're away.</span>
        <button data-testid={`resume-habit-${habit.id}`} onClick={resume} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-900 hover:text-sky-700 whitespace-nowrap">
          <PlayCircle className="w-4 h-4" /> Resume
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {!open ? (
        <button data-testid={`pause-habit-${habit.id}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 font-semibold">
          <PauseCircle className="w-3.5 h-3.5" /> Pause for a while
        </button>
      ) : (
        <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3 space-y-2" data-testid={`pause-form-${habit.id}`}>
          <div className="text-xs text-stone-600">Away, sick, or overloaded? Pause and nothing counts as missed until you're back.</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input data-testid={`pause-until-${habit.id}`} type="date" value={until} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)}
              className="px-3 py-1.5 rounded-xl border border-stone-300 text-xs" />
            <select data-testid={`pause-reason-${habit.id}`} value={reason} onChange={(e) => setReason(e.target.value)} className="px-3 py-1.5 rounded-xl border border-stone-300 text-xs bg-white">
              {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button data-testid={`pause-confirm-${habit.id}`} onClick={pause} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-stone-900 text-white">Pause</button>
            <button onClick={() => setOpen(false)} className="text-xs text-stone-500">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
